import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { Icon } from "@astryxdesign/core/Icon";
import { Item } from "@astryxdesign/core/Item";
import { HStack } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import type { Idea } from "../../shared/types";
import { CATEGORY_META, dueInfo, faviconUrl } from "../lib/format";
import { GripIcon, PinIcon, SparkleIcon } from "../lib/icons";
import type { ReorderHandlers } from "../lib/useReorder";
import { SwipeableRow } from "./SwipeableRow";

interface Props {
  idea: Idea;
  onOpen: (id: string) => void;
  onToggleDone: (idea: Idea) => void;
  onDelete: (idea: Idea) => void;
  /** Present only for pinned rows: enables the drag handle. */
  reorder?: ReorderHandlers;
  isDragging?: boolean;
}

export function IdeaRow({ idea, onOpen, onToggleDone, onDelete, reorder, isDragging }: Props) {
  const done = idea.status === "done";
  const cat = CATEGORY_META[idea.category];
  const due = done ? null : dueInfo(idea.due);
  const firstLink = idea.links[0];
  const checked = idea.checklist.filter((c) => c.done).length;

  const meta: React.ReactNode[] = [];
  meta.push(<Token key="cat" size="sm" color={cat.color} label={cat.label} />);
  if (due) meta.push(<Token key="due" size="sm" color={due.color} label={due.label} />);
  if (firstLink) {
    meta.push(
      <Token
        key="link"
        size="sm"
        color="gray"
        icon={
          <img
            src={faviconUrl(firstLink.domain)}
            alt=""
            width={12}
            height={12}
            style={{ borderRadius: 2, display: "block" }}
          />
        }
        label={idea.links.length > 1 ? `${firstLink.domain} +${idea.links.length - 1}` : firstLink.domain}
      />,
    );
  }
  if (idea.checklist.length > 0) {
    meta.push(
      <Text key="check" type="supporting" size="xsm">
        {checked}/{idea.checklist.length} steps
      </Text>,
    );
  }
  if (meta.length <= 1 && idea.notes) {
    meta.push(
      <Text key="notes" type="supporting" size="xsm" maxLines={1}>
        {idea.notes}
      </Text>,
    );
  }

  return (
    <SwipeableRow
      onSwipeRight={() => onToggleDone(idea)}
      onSwipeLeft={() => onDelete(idea)}
      isDragging={isDragging === true}
    >
      <Item
        density="balanced"
        onClick={() => onOpen(idea.id)}
      startContent={
        // Bubble-phase stop: the checkbox handles the click first, then we keep
        // it from reaching the row's open-handler.
        <span onClick={(e) => e.stopPropagation()}>
          <CheckboxInput
            label={done ? "Reopen" : "Mark done"}
            isLabelHidden
            size="sm"
            value={done}
            onChange={() => onToggleDone(idea)}
          />
        </span>
      }
      label={
        <Text
          type="body"
          weight="medium"
          hasStrikethrough={done}
          color={done ? "secondary" : "primary"}
          maxLines={1}
        >
          {idea.title}
        </Text>
      }
      description={
        <HStack gap={1.5} align="center" wrap="wrap">
          {meta}
        </HStack>
      }
      endContent={
        idea.pinned || idea.suggestions ? (
          <HStack gap={1} align="center">
            {idea.suggestions ? (
              <Icon icon={SparkleIcon} color="accent" size="sm" label="Has AI suggestions" />
            ) : null}
            {reorder ? (
              <span
                className="hq-drag-handle"
                role="button"
                tabIndex={-1}
                aria-label="Drag to reorder"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={reorder.onPointerDown}
              >
                <Icon icon={GripIcon} color="secondary" size="sm" />
              </span>
            ) : idea.pinned ? (
              <Icon icon={PinIcon} color="accent" size="sm" label="Pinned" />
            ) : null}
          </HStack>
        ) : undefined
      }
      />
    </SwipeableRow>
  );
}
